import type { ClubApplication } from '@kk/domain';
import { Card, Text, Title2, WasabiButton } from '@kk/ui';
import { useState } from 'react';

import { useContentCan } from '@/auth/SessionProvider';
import { LinkButton, Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import {
  useApproveClubApplication,
  useClubApplications,
  useDeclineClubApplication,
  useErrorMessage,
} from '@/data/hooks';

const c = deck.o18;

/**
 * **The admin's Applications page** (O18; Evan, 2026-09-27). Owners only. Approve sets the club up and emails its
 * director the set-up link; Decline sends a kind email, and they can apply again. Declined ones are deleted after
 * 12 months.
 */
export function ApplicationsScreen() {
  const owner = useContentCan('team');
  const list = useClubApplications(owner);
  const errorMessage = useErrorMessage();

  if (!owner) return <Status testID="applications-owners-only">{c.ownersOnly}</Status>;
  const rows = list.data ?? [];
  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{c.title}</Title2>
          <Text variant="bodySm" tone="muted">
            {c.subtitle}
          </Text>
        </div>
      </div>
      {list.error ? <Status testID="applications-error">{errorMessage(list.error)}</Status> : null}
      {list.isSuccess && rows.length === 0 ? (
        <Status testID="applications-empty">{c.empty}</Status>
      ) : null}
      <div className="stack">
        {rows.map((a) => (
          <ApplicationCard key={a.id} a={a} />
        ))}
      </div>
    </>
  );
}

function ApplicationCard({ a }: { a: ClubApplication }) {
  const approve = useApproveClubApplication();
  const decline = useDeclineClubApplication();
  const errorMessage = useErrorMessage();
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const failed = approve.error ?? decline.error;
  const line = (label: string, value: string | undefined) =>
    value ? (
      <Text variant="bodySm" tone="ink">
        <span className="field-label">{label}</span> {value}
      </Text>
    ) : null;
  return (
    <Card>
      <div className="stack" data-testid={`application-${a.id}`}>
        <div className="row-between">
          <Text variant="labelStrong">{a.clubName}</Text>
          <span className={`pill pill-${a.status}`} data-testid={`application-status-${a.id}`}>
            {c.status[a.status]}
          </span>
        </div>
        {a.referredBy ? (
          <Text variant="bodySm" tone="muted">
            {c.referredBy(a.referredBy.villageName)}
          </Text>
        ) : null}
        {line(c.fields.contactName, a.contactName)}
        {line(c.fields.email, a.email)}
        {line(c.fields.phone, a.phone)}
        {line(c.fields.cityState, a.cityState)}
        {line(c.fields.kidsCount, a.kidsCount)}
        {line(c.fields.kidsAges, a.kidsAges)}
        {line(c.fields.orgType, c.orgTypes[a.orgType])}
        {line(c.fields.heardFrom, a.heardFrom)}
        {line(c.fields.website, a.website)}
        {a.status === 'new' && !confirming ? (
          <div className="actions">
            <WasabiButton
              label={c.approve}
              size="sm"
              fullWidth={false}
              onPress={() =>
                approve.mutate(a.id, { onSuccess: () => setNote(c.approved(a.clubName)) })
              }
              testID={`application-approve-${a.id}`}
            />
            <LinkButton
              label={c.decline}
              onPress={() => setConfirming(true)}
              testID={`application-decline-${a.id}`}
            />
          </div>
        ) : null}
        {confirming ? (
          <div className="stack" data-testid={`application-confirm-${a.id}`}>
            <Text variant="bodySm">{c.declineConfirm}</Text>
            <div className="actions">
              <WasabiButton
                label={c.decline}
                size="sm"
                fullWidth={false}
                onPress={() =>
                  decline.mutate(a.id, {
                    onSuccess: () => {
                      setConfirming(false);
                      setNote(c.declinedNote);
                    },
                  })
                }
                testID={`application-decline-confirm-${a.id}`}
              />
              <LinkButton
                label={c.keepIt}
                onPress={() => setConfirming(false)}
                testID={`application-keep-${a.id}`}
              />
            </div>
          </div>
        ) : null}
        {note ? <Status testID={`application-note-${a.id}`}>{note}</Status> : null}
        {failed ? (
          <Status testID={`application-error-${a.id}`}>{errorMessage(failed)}</Status>
        ) : null}
      </div>
    </Card>
  );
}
